import type { FilterState } from '@/components/filter-bar/filter-bar';
import type { TaskInboxSearchRequest, TaskStatus, TasksInboxPreference } from '@vagusx/clients';

// ---------------------------------------------------------------------------
// URL ↔ FilterState conversions
// ---------------------------------------------------------------------------

/**
 * Convert URL search params into FilterState for the FilterBar component.
 *
 * Only values that match known filter options are included (unknown values
 * are silently dropped). When no URL params are present, the default
 * state is `status: ["open"], show: ["my"]` — a fresh /tasks visit lands
 * on the user's own open queue.
 */
export function urlToFilterState(params: Record<string, string | undefined>): FilterState {
  const state: FilterState = {};
  const noParams = !hasAnyFilterParam(params);

  // Status — defaults to 'open' whenever the URL omits a status param.
  // Persists the implicit "hide done" filter across tab/scope/label
  // changes; the user has to opt into seeing done by setting
  // `?status=done` (or `open,done`) explicitly. Without this, clicking
  // the Un / All tab from a bare URL silently drops the status filter
  // and the inbox starts showing completed tasks.
  if (params.status) {
    const values = params.status.split(',').filter((v) => VALID_STATUS.has(v));
    if (values.length > 0) {
      state.status = { type: 'multi-select', values };
    }
  } else {
    state.status = { type: 'multi-select', values: ['open'] };
  }

  // Show — single-select tab in the new UI; multi-select kept as the
  // wire shape for backward URL compatibility (`show=my,all` collapses
  // to `all` server-side anyway).
  if (params.show) {
    const values = params.show.split(',').filter((v) => VALID_SHOW.has(v));
    if (values.length > 0) {
      state.show = { type: 'multi-select', values };
    }
  } else if (noParams) {
    state.show = { type: 'multi-select', values: ['my'] };
  }

  // Labels
  if (params.labels) {
    const values = params.labels.split(',').filter(Boolean);
    if (values.length > 0) {
      state.labels = { type: 'multi-select', values };
    }
  }

  // Date range
  if (params.dateFrom || params.dateTo) {
    state.date = {
      type: 'date-range',
      from: params.dateFrom ?? '',
      to: params.dateTo ?? '',
    };
  }

  // Assignee — synthesize the multi-select value from the (assignedTo,
  // assigneeType) pair. The pair is the canonical URL contract; the
  // FilterBar primitive consumes a single string per option, so we
  // encode as `<type>:<id>`.
  if (params.assignedTo) {
    const type = params.assigneeType ?? 'user';
    state.assignee = { type: 'multi-select', values: [`${type}:${params.assignedTo}`] };
  }

  return state;
}

/**
 * Convert FilterState back into URL search params.
 * Multi-select values are comma-joined. Empty filters are omitted.
 */
export function filterStateToUrl(state: FilterState): Record<string, string | undefined> {
  const params: Record<string, string | undefined> = {};

  // Status
  const statusFilter = state.status;
  if (statusFilter?.type === 'multi-select' && statusFilter.values.length > 0) {
    params.status = statusFilter.values.join(',');
  }

  // Show
  const showFilter = state.show;
  if (showFilter?.type === 'multi-select' && showFilter.values.length > 0) {
    params.show = showFilter.values.join(',');
  }

  // Labels
  const labelsFilter = state.labels;
  if (labelsFilter?.type === 'multi-select' && labelsFilter.values.length > 0) {
    params.labels = labelsFilter.values.join(',');
  }

  // Date range
  const dateFilter = state.date;
  if (dateFilter?.type === 'date-range') {
    if (dateFilter.from) params.dateFrom = dateFilter.from;
    if (dateFilter.to) params.dateTo = dateFilter.to;
  }

  // Assignee — first selection wins (the API takes a single
  // `assignedTo` today). Decode the `<type>:<id>` opaque string back
  // into the canonical URL pair.
  const assigneeFilter = state.assignee;
  if (assigneeFilter?.type === 'multi-select' && assigneeFilter.values.length > 0) {
    const first = assigneeFilter.values[0];
    if (first) {
      const idx = first.indexOf(':');
      if (idx > 0) {
        params.assigneeType = first.slice(0, idx);
        params.assignedTo = first.slice(idx + 1);
      }
    }
  }

  return params;
}

// ---------------------------------------------------------------------------
// FilterState → API request params
// ---------------------------------------------------------------------------

/**
 * Convert URL search params into a TaskInboxSearchRequest. Defaults mirror
 * `urlToFilterState`:
 * - status defaults to 'open' whenever the URL omits a status param —
 *   persists across tab/scope/label changes so completed tasks stay
 *   hidden until explicitly requested via `?status=done` or `open,done`.
 * - show defaults to 'my' only on a bare URL (no params at all). Any
 *   non-empty URL without `show` is treated as the `all` scope so shared
 *   links don't silently apply the recipient's "mine" filter.
 *
 * Value mappings:
 * - Status "open" → ['open', 'in_progress'], "done" → ['completed', 'cancelled']
 * - Show "my" → mine: true (matches direct assignment OR via team membership);
 *   "all" or both selected → no mine filter
 */
export function filterStateToApiParams(
  params: Record<string, string | undefined>
): Omit<TaskInboxSearchRequest, 'limit' | 'offset'> {
  const result: Omit<TaskInboxSearchRequest, 'limit' | 'offset'> = {};
  const noParams = !hasAnyFilterParam(params);

  // Status — same default rule as `urlToFilterState`: 'open' when the
  // URL omits status, regardless of whether other filter params are set.
  // Without this the Un / All tab counts include completed tasks because
  // a tab click drops the implicit status=open filter the chip UI claims
  // to show.
  const rawStatus = params.status ?? 'open';
  if (rawStatus) {
    const statusValues = rawStatus.split(',');
    const apiStatuses: TaskStatus[] = [];
    for (const v of statusValues) {
      const mapped = STATUS_TO_API[v];
      if (mapped) {
        apiStatuses.push(...mapped);
      }
    }
    if (apiStatuses.length > 0) {
      result.status = [...new Set(apiStatuses)];
    }
  }

  // Show → mine / unassigned (mutually exclusive scope flags). When the
  // URL has both `my` and `all` selected we treat that as `all` — no
  // mine filter — matching the multi-select semantics from before tabs.
  const rawShow = params.show ?? (noParams ? 'my' : undefined);
  if (rawShow) {
    const showValues = rawShow.split(',');
    const hasAll = showValues.includes('all');
    const hasMy = showValues.includes('my');
    const hasUnassigned = showValues.includes('unassigned');
    if (hasUnassigned) {
      result.unassigned = true;
    } else if (hasMy && !hasAll) {
      result.mine = true;
    }
  }

  // Labels: pass through
  if (params.labels) {
    const labelValues = params.labels.split(',').filter(Boolean);
    if (labelValues.length > 0) {
      result.labels = labelValues;
    }
  }

  // Date range
  if (params.dateFrom) {
    result.dateFrom = params.dateFrom;
  }
  if (params.dateTo) {
    result.dateTo = params.dateTo;
  }

  return result;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const VALID_STATUS = new Set(['open', 'done']);
const VALID_SHOW = new Set(['my', 'all', 'unassigned']);

/** Scope tab values rendered by the task-list header. */
export type TaskScope = 'my' | 'all' | 'unassigned';

/**
 * Read the active scope tab from URL params. Defaults to 'my' on bare
 * URL (matches `urlToFilterState`'s default state), 'all' when the
 * filter explicitly has multiple scopes (collapsed view).
 */
export function urlToScope(params: Record<string, string | undefined>): TaskScope {
  const noParams = !hasAnyFilterParam(params);
  const raw = params.show ?? (noParams ? 'my' : undefined);
  if (!raw) return 'all';
  const values = raw.split(',');
  if (values.includes('unassigned')) return 'unassigned';
  if (values.includes('all') || values.length > 1) return 'all';
  if (values.includes('my')) return 'my';
  return 'all';
}

// VagusX usage in this org has only two statuses on actual rows:
// 'open' and 'completed'. The schema enum has more values (pending,
// in_progress, cancelled, failed) but they're unused in operator
// workflows. Keeping the buckets aligned to what the data actually
// contains avoids dashboard counts disagreeing with inbox totals.
const STATUS_TO_API: Record<string, TaskStatus[]> = {
  open: ['open'],
  done: ['completed'],
};

export function hasAnyFilterParam(params: Record<string, string | undefined>): boolean {
  return !!(
    params.status ||
    params.show ||
    params.labels ||
    params.dateFrom ||
    params.dateTo ||
    params.assignedTo
  );
}

// ---------------------------------------------------------------------------
// FilterState ↔ TasksInboxPreference (for cross-session persistence)
//
// The persisted shape mirrors the URL params — not the FilterState — so
// a saved preference round-trips through a navigate() cleanly. Search
// term, page, and selected task id are intentionally excluded.
// ---------------------------------------------------------------------------

/**
 * Snapshot the current URL filter params into the persistence shape.
 * Empty keys are omitted so the saved blob stays minimal.
 */
export function urlToTasksInboxPreference(
  params: Record<string, string | undefined>
): TasksInboxPreference {
  const pref: TasksInboxPreference = {};
  if (params.status) pref.status = params.status;
  if (params.show) pref.show = params.show;
  if (params.labels) pref.labels = params.labels;
  if (params.dateFrom) pref.dateFrom = params.dateFrom;
  if (params.dateTo) pref.dateTo = params.dateTo;
  if (params.assignedTo) pref.assignedTo = params.assignedTo;
  if (params.assigneeType === 'user' || params.assigneeType === 'team') {
    pref.assigneeType = params.assigneeType;
  }
  return pref;
}

/**
 * Inverse of `urlToTasksInboxPreference` — rebuild URL params from a
 * saved preference blob. Used on first-mount redirect when the URL is
 * bare but the user has saved filters.
 */
export function tasksInboxPreferenceToUrl(pref: TasksInboxPreference): Record<string, string> {
  const params: Record<string, string> = {};
  if (pref.status) params.status = pref.status;
  if (pref.show) params.show = pref.show;
  if (pref.labels) params.labels = pref.labels;
  if (pref.dateFrom) params.dateFrom = pref.dateFrom;
  if (pref.dateTo) params.dateTo = pref.dateTo;
  if (pref.assignedTo) params.assignedTo = pref.assignedTo;
  if (pref.assigneeType) params.assigneeType = pref.assigneeType;
  return params;
}
